import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { toast } from '@/ui/toast';
import { createAiAnalysisToastId } from '@/ui/toast-ids';
import { AiSelectedAnalysisProgressEventEntity } from '@/entities/ai-selected-analysis-progress-event.entity';
import { SocketIoEvents } from '@/events/socket-io.events';
import { Sort } from '@/types/sort.type';
import { AiSelectedAnalysisProgress } from '@/context/reviews-page.context';
import { useAiAnalysisFinalization } from '@/components/action-buttons/use-ai-analysis-finalization';
import { useAiAnalysisProgressEventsEffect } from '@/components/action-buttons/use-ai-analysis-progress-events.effect';
import { createJobsRepository } from '@/container/jobs-repository';
import { QUEUE_JOBS, QUEUE_NAMES } from '@/constants/queue';
import { useRefreshClientAiQuota } from '@/hooks/use-refresh-client-ai-quota';
import { formatMarketplaceApiErrorMessageFromUnknown } from '@/shared/errors/marketplace-api-error-message';
import { resolveAiSelectedAnalysisRetryingMessage } from '@/shared/ai-selected-analysis-progress-messages';
import { resolveOperationId } from '@/shared/operation-id';
import { calculateEtaSeconds } from '@/shared/progress-estimation';
import { useMarketplaceApi } from '@/context/marketplace.context';

export type AiSelectedAnalysisEnqueueResponse = {
    data: {
        operationId: string;
        startedAt: number;
        total: number;
        limitWarning: string | null;
    };
};

type UseAiAnalysisOrchestrationParams = {
    events: SocketIoEvents;
    accessToken: string;
    filtersQuery: string[];
    sort: Sort;
    selectionSessionId: string;
    isAiAnalysisLoading: boolean;
    isSocketConnected: boolean;
    setPickedReviews?: (val: string[]) => void;
    setAiAnalysisProgress: Dispatch<SetStateAction<AiSelectedAnalysisProgress | null>>;
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    refetchReviewsAndProducts: () => Promise<void>;
    onAiAnalysisFinalized?: (event: AiSelectedAnalysisProgressEventEntity) => void;
};

type UseAiAnalysisOrchestrationReturn = {
    startAiAnalysis: (
        initialTotal: number,
        enqueueRequest: (operationId: string) => Promise<AiSelectedAnalysisEnqueueResponse>,
    ) => Promise<void>;
    cancelAiAnalysis: () => Promise<void>;
};

export const AI_ANALYSIS_CANCEL_FINALIZATION_TIMEOUT_MS = 15_000;
const AI_ANALYSIS_PROGRESS_RECOVERY_INTERVAL_MS = 3_000;
const SELECTED_REVIEWS_ALREADY_ANALYZED_MESSAGE = 'У выбранных отзывов уже есть ИИ-анализ';
const AI_ANALYSIS_NOOP_SUCCESS_MESSAGE = 'Нечего обрабатывать';

const aiAnalysisLogger = createClientLogger({
    scope: 'ai-analysis',
});

export const useAiAnalysisOrchestration = ({
    events,
    accessToken,
    filtersQuery,
    sort,
    selectionSessionId,
    isAiAnalysisLoading,
    isSocketConnected,
    setPickedReviews,
    setAiAnalysisProgress,
    enqueueReviewSelectionMutation,
    refetchReviewsAndProducts,
    onAiAnalysisFinalized,
}: UseAiAnalysisOrchestrationParams): UseAiAnalysisOrchestrationReturn => {
    const { reviewsApi } = useMarketplaceApi();
    const refreshClientAiQuota = useRefreshClientAiQuota();
    const aiAnalysisOperationIdRef = useRef<string | null>(null);
    const aiAnalysisProgressRecoveryOperationIdRef = useRef<string | null>(null);
    const aiAnalysisFinalizingOperationIdRef = useRef<string | null>(null);
    const aiAnalysisCancelFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const aiAnalysisNoopCleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const aiAnalysisProgressSnapshotRef = useRef<AiSelectedAnalysisProgress | null>(null);

    const clearAiAnalysisCancelFallbackTimer = useCallback(() => {
        if (aiAnalysisCancelFallbackTimerRef.current) {
            clearTimeout(aiAnalysisCancelFallbackTimerRef.current);
            aiAnalysisCancelFallbackTimerRef.current = null;
        }
    }, []);

    const clearAiAnalysisNoopCleanupTimer = useCallback(() => {
        if (aiAnalysisNoopCleanupTimerRef.current) {
            clearTimeout(aiAnalysisNoopCleanupTimerRef.current);
            aiAnalysisNoopCleanupTimerRef.current = null;
        }
    }, []);

    const setAiAnalysisProgressWithSnapshot = useCallback(
        (updater: SetStateAction<AiSelectedAnalysisProgress | null>) => {
            setAiAnalysisProgress((current) => {
                const nextProgress =
                    typeof updater === 'function'
                        ? (
                              updater as (
                                  previous: AiSelectedAnalysisProgress | null,
                              ) => AiSelectedAnalysisProgress | null
                          )(current)
                        : updater;

                aiAnalysisProgressSnapshotRef.current = nextProgress;
                return nextProgress;
            });
        },
        [setAiAnalysisProgress],
    );

    useEffect(() => {
        return () => {
            clearAiAnalysisCancelFallbackTimer();
            clearAiAnalysisNoopCleanupTimer();
            aiAnalysisOperationIdRef.current = null;
            aiAnalysisProgressRecoveryOperationIdRef.current = null;
            aiAnalysisFinalizingOperationIdRef.current = null;
            aiAnalysisProgressSnapshotRef.current = null;
        };
    }, [clearAiAnalysisCancelFallbackTimer, clearAiAnalysisNoopCleanupTimer]);

    const applyAiSelectedAnalysisProgress = useCallback(
        (event: AiSelectedAnalysisProgressEventEntity) => {
            setAiAnalysisProgressWithSnapshot((current) => {
                if (aiAnalysisOperationIdRef.current !== event.operationId) {
                    return current;
                }

                const startedAt = current?.startedAt ?? event.startedAt;
                const total = event.total;
                const nextSuccessCount = Math.max(current?.successCount ?? 0, event.successCount);
                const nextFailedCount = Math.max(current?.failedCount ?? 0, event.failedCount);
                const processed = Math.min(
                    total,
                    Math.max(current?.processed ?? 0, event.processed, nextSuccessCount + nextFailedCount),
                );
                const isRunning = event.stage !== 'completed' && event.stage !== 'failed' && event.stage !== 'canceled';
                const isRetrying = event.stage === 'retrying';

                return {
                    isRunning,
                    startedAt,
                    total,
                    processed,
                    successCount: nextSuccessCount,
                    failedCount: nextFailedCount,
                    etaSeconds: isRunning ? calculateEtaSeconds(startedAt, processed, total) : 0,
                    retryMessage: isRetrying ? resolveAiSelectedAnalysisRetryingMessage({ event }) : null,
                    retryAttempt: isRetrying ? (event.retryAttempt ?? null) : null,
                    maxAttempts: isRetrying ? (event.maxAttempts ?? null) : null,
                    retryAt: isRetrying ? (event.retryAt ?? null) : null,
                    retryDelayMs: isRetrying ? (event.retryDelayMs ?? null) : null,
                };
            });
        },
        [setAiAnalysisProgressWithSnapshot],
    );

    const { finishAiSelectedAnalysis } = useAiAnalysisFinalization({
        accessToken,
        filtersQuery,
        sort,
        selectionSessionId,
        setPickedReviews,
        setAiAnalysisProgress: setAiAnalysisProgressWithSnapshot,
        enqueueReviewSelectionMutation,
        refetchReviewsAndProducts,
        aiAnalysisOperationIdRef,
        aiAnalysisFinalizingOperationIdRef,
        applyAiSelectedAnalysisProgress,
        onAiAnalysisFinalized,
    });
    const finishAiSelectedAnalysisWithCleanup = useCallback(
        async (event: AiSelectedAnalysisProgressEventEntity) => {
            clearAiAnalysisCancelFallbackTimer();
            try {
                await finishAiSelectedAnalysis(event);
            } finally {
                if (aiAnalysisProgressRecoveryOperationIdRef.current === event.operationId) {
                    aiAnalysisProgressRecoveryOperationIdRef.current = null;
                }
            }
        },
        [clearAiAnalysisCancelFallbackTimer, finishAiSelectedAnalysis],
    );

    const handleAiSelectedAnalysisProgress = useCallback(
        (event: AiSelectedAnalysisProgressEventEntity) => {
            if (event.stage === 'completed' || event.stage === 'failed' || event.stage === 'canceled') {
                void finishAiSelectedAnalysisWithCleanup(event);
                return;
            }

            applyAiSelectedAnalysisProgress(event);
        },
        [applyAiSelectedAnalysisProgress, finishAiSelectedAnalysisWithCleanup],
    );

    const recoverAiSelectedAnalysisProgress = useCallback(
        async (operationId: string) => {
            try {
                const response = await reviewsApi.getAiSelectedAnalysisProgress(operationId, {
                    authToken: accessToken,
                });
                const progress = response.data.progress;
                if (
                    !progress ||
                    progress.operationId !== operationId ||
                    aiAnalysisOperationIdRef.current !== operationId
                ) {
                    return;
                }

                handleAiSelectedAnalysisProgress(progress);
            } catch (error) {
                if (aiAnalysisOperationIdRef.current !== operationId) {
                    return;
                }

                logClientError({
                    logger: aiAnalysisLogger,
                    message:
                        '[client.ai-analysis.selection-progress-recovery] Failed to load selected reviews AI analysis progress snapshot.',
                    error,
                    context: {
                        operationId,
                    },
                });
            }
        },
        [accessToken, handleAiSelectedAnalysisProgress, reviewsApi],
    );

    const scheduleAiAnalysisCancelFallback = useCallback(
        (operationId: string) => {
            clearAiAnalysisCancelFallbackTimer();
            aiAnalysisCancelFallbackTimerRef.current = setTimeout(() => {
                aiAnalysisCancelFallbackTimerRef.current = null;
                if (aiAnalysisOperationIdRef.current !== operationId) {
                    return;
                }
                if (aiAnalysisFinalizingOperationIdRef.current === operationId) {
                    return;
                }

                const progress = aiAnalysisProgressSnapshotRef.current;
                const total = progress ? Math.max(progress.total, progress.processed) : 0;

                void finishAiSelectedAnalysisWithCleanup({
                    clientId: '',
                    operationId,
                    stage: 'canceled',
                    startedAt: progress?.startedAt ?? Date.now(),
                    processed: progress?.processed ?? 0,
                    total,
                    successCount: progress?.successCount ?? 0,
                    failedCount: progress?.failedCount ?? 0,
                    errorMessage: null,
                });
            }, AI_ANALYSIS_CANCEL_FINALIZATION_TIMEOUT_MS);
        },
        [clearAiAnalysisCancelFallbackTimer, finishAiSelectedAnalysisWithCleanup],
    );

    const scheduleAiAnalysisNoopCleanup = useCallback(
        (progressStartedAt: number) => {
            clearAiAnalysisNoopCleanupTimer();
            aiAnalysisNoopCleanupTimerRef.current = setTimeout(() => {
                aiAnalysisNoopCleanupTimerRef.current = null;
                setAiAnalysisProgressWithSnapshot((current) => {
                    if (!current || current.startedAt !== progressStartedAt || current.isRunning) {
                        return current;
                    }

                    if (current.total !== 0 || current.processed !== 0) {
                        return current;
                    }

                    return null;
                });
            }, 0);
        },
        [clearAiAnalysisNoopCleanupTimer, setAiAnalysisProgressWithSnapshot],
    );

    useAiAnalysisProgressEventsEffect({
        events,
        aiAnalysisOperationIdRef,
        handleAiSelectedAnalysisProgress,
    });

    useEffect(() => {
        const operationId = aiAnalysisOperationIdRef.current;
        if (!operationId || aiAnalysisProgressRecoveryOperationIdRef.current !== operationId) {
            return;
        }

        void recoverAiSelectedAnalysisProgress(operationId);
    }, [isSocketConnected, recoverAiSelectedAnalysisProgress]);

    useEffect(() => {
        const recoveryTimer = setInterval(() => {
            const operationId = aiAnalysisOperationIdRef.current;
            if (!operationId || aiAnalysisProgressRecoveryOperationIdRef.current !== operationId) {
                return;
            }

            void recoverAiSelectedAnalysisProgress(operationId);
        }, AI_ANALYSIS_PROGRESS_RECOVERY_INTERVAL_MS);

        return () => clearInterval(recoveryTimer);
    }, [recoverAiSelectedAnalysisProgress]);

    const startAiAnalysis = useCallback(
        async (
            initialTotal: number,
            enqueueRequest: (operationId: string) => Promise<AiSelectedAnalysisEnqueueResponse>,
        ) => {
            if (isAiAnalysisLoading || aiAnalysisOperationIdRef.current) {
                return;
            }

            const operationId = resolveOperationId(undefined, Date.now());
            const startedAt = Date.now();

            clearAiAnalysisCancelFallbackTimer();
            clearAiAnalysisNoopCleanupTimer();
            aiAnalysisOperationIdRef.current = operationId;
            aiAnalysisProgressRecoveryOperationIdRef.current = null;
            aiAnalysisFinalizingOperationIdRef.current = null;
            setAiAnalysisProgressWithSnapshot({
                isRunning: true,
                startedAt,
                total: initialTotal,
                processed: 0,
                successCount: 0,
                failedCount: 0,
                etaSeconds: null,
                retryMessage: null,
                retryAttempt: null,
                maxAttempts: null,
                retryAt: null,
                retryDelayMs: null,
            });

            try {
                const response = await enqueueRequest(operationId);
                const resolvedOperationId = response.data.operationId;
                if (aiAnalysisOperationIdRef.current !== operationId) {
                    return;
                }

                aiAnalysisOperationIdRef.current = resolvedOperationId;
                aiAnalysisProgressRecoveryOperationIdRef.current = resolvedOperationId;

                setAiAnalysisProgressWithSnapshot((current) => {
                    if (aiAnalysisOperationIdRef.current !== resolvedOperationId) {
                        return current;
                    }

                    if (!current) {
                        return current;
                    }

                    const processed = Math.min(current.processed, response.data.total);
                    return {
                        ...current,
                        startedAt: response.data.startedAt,
                        total: response.data.total,
                        processed,
                        etaSeconds: calculateEtaSeconds(response.data.startedAt, processed, response.data.total),
                    };
                });

                if (response.data.limitWarning) {
                    toast.warning(response.data.limitWarning, {
                        autoClose: false,
                        toastId: createAiAnalysisToastId({
                            operationId: resolvedOperationId,
                            outcome: 'limit-warning',
                        }),
                    });
                }
                void recoverAiSelectedAnalysisProgress(resolvedOperationId);
                await refreshClientAiQuota();
            } catch (error) {
                await refreshClientAiQuota();
                const errorMessage = formatMarketplaceApiErrorMessageFromUnknown(error);
                const isAlreadyAnalyzedNoop = errorMessage?.startsWith(SELECTED_REVIEWS_ALREADY_ANALYZED_MESSAGE);
                const isOfferPolicyBlock = logOfferNotAcceptedMarketplacePolicyWarning({
                    logger: aiAnalysisLogger,
                    message: '[client.ai-analysis.offer-policy] AI analysis start blocked before offer acceptance.',
                    error,
                    context: {
                        action: 'ai_analysis.enqueue',
                        operationId,
                        initialTotal,
                    },
                });

                if (!isAlreadyAnalyzedNoop && !isOfferPolicyBlock) {
                    logClientError({
                        logger: aiAnalysisLogger,
                        message: '[client.ai-analysis.selection-start] Failed to start selected reviews AI analysis.',
                        error,
                        context: {
                            operationId,
                            initialTotal,
                        },
                    });
                }
                if (aiAnalysisOperationIdRef.current === operationId) {
                    aiAnalysisOperationIdRef.current = null;
                }
                if (aiAnalysisProgressRecoveryOperationIdRef.current === operationId) {
                    aiAnalysisProgressRecoveryOperationIdRef.current = null;
                }
                if (aiAnalysisFinalizingOperationIdRef.current === operationId) {
                    aiAnalysisFinalizingOperationIdRef.current = null;
                }
                if (isAlreadyAnalyzedNoop) {
                    setAiAnalysisProgressWithSnapshot((current) => {
                        if (!current || current.startedAt !== startedAt) {
                            return current;
                        }

                        return {
                            ...current,
                            isRunning: false,
                            total: 0,
                            processed: 0,
                            successCount: 0,
                            failedCount: 0,
                            etaSeconds: 0,
                        };
                    });
                    scheduleAiAnalysisNoopCleanup(startedAt);
                    toast.success(AI_ANALYSIS_NOOP_SUCCESS_MESSAGE);
                    return;
                }

                setAiAnalysisProgressWithSnapshot((current) => {
                    if (!current) {
                        return current;
                    }

                    if (current.startedAt !== startedAt) {
                        return current;
                    }

                    if (aiAnalysisOperationIdRef.current !== null && aiAnalysisOperationIdRef.current !== operationId) {
                        return current;
                    }

                    return null;
                });
                toast.error(errorMessage || 'Не удалось запустить массовый ИИ-анализ', {
                    autoClose: false,
                    toastId: createAiAnalysisToastId({
                        operationId,
                        outcome: 'start-failed',
                    }),
                });
            }
        },
        [
            clearAiAnalysisCancelFallbackTimer,
            clearAiAnalysisNoopCleanupTimer,
            isAiAnalysisLoading,
            recoverAiSelectedAnalysisProgress,
            refreshClientAiQuota,
            scheduleAiAnalysisNoopCleanup,
            setAiAnalysisProgressWithSnapshot,
        ],
    );

    const cancelAiAnalysis = useCallback(async () => {
        const operationId = aiAnalysisOperationIdRef.current;
        if (!operationId) {
            return;
        }

        const jobsRepository = createJobsRepository();

        try {
            await jobsRepository.cancel(
                {
                    jobNames: [QUEUE_JOBS.analyzeSelectedReviewsAi],
                    queueName: QUEUE_NAMES.aiAnalysis,
                    operationId,
                },
                { authToken: accessToken },
            );
        } catch (error) {
            logClientError({
                logger: aiAnalysisLogger,
                message: '[client.ai-analysis.selection-cancel] Failed to cancel selected reviews AI analysis.',
                error,
                context: {
                    operationId,
                },
            });
            toast.error('Не удалось отменить массовый ИИ-анализ');
            return;
        }

        setAiAnalysisProgressWithSnapshot((current) => {
            if (!current) {
                return current;
            }

            return {
                ...current,
                isRunning: true,
            };
        });
        scheduleAiAnalysisCancelFallback(operationId);
        toast.success('Остановка массового ИИ-анализа запрошена');
    }, [accessToken, scheduleAiAnalysisCancelFallback, setAiAnalysisProgressWithSnapshot]);

    return {
        startAiAnalysis,
        cancelAiAnalysis,
    };
};
